import Markdown from "marked-react";
import React, { useState, useEffect, useRef } from "react";

// marked-react parses the markdown source into a fresh React subtree on every
// render — this is the single most expensive operation in the AI chat. Wrapping
// it in React.memo (keyed on the source string) means the parse only re-runs
// when the text actually changes, NOT on every unrelated AIChat re-render
// (its scroll / animation / rAF / debounce effects fire constantly and used to
// re-parse every accumulated message's markdown → the "froze at Q3/4"
// accumulation cost). Shared by the streaming (AiDelta) and completed-message
// (AIChat) render paths. (FOX-3611)
const MemoMarkdownComponent = ({ children }: { children?: string }) => (
  <Markdown>{children}</Markdown>
);
export const MemoMarkdown = React.memo(MemoMarkdownComponent);

export const AiDeltaComponent = ({
  useAIDelta,
  idempotencyID,
  markdown,
  render,
}: {
  useAIDelta: (idempotencyID: string) => string;
  idempotencyID?: string;
  markdown?: boolean;
  render?: (text: string) => React.ReactNode;
}) => {
  const message = useAIDelta(idempotencyID || "");

  // Streaming markdown path: render the accumulated streamed text as markdown
  // ONCE per delta frame (O(frames·N)) instead of routing it through
  // TypingEffect's setInterval(…,1ms) which re-parsed the entire growing string
  // through marked-react on every character tick (O(N²) — the Safari CPU/GC
  // hang). Mirrors the proven ask-coach.tsx per-frame <Markdown> + CSS cursor
  // pattern. MemoMarkdown caches the parse so unrelated parent re-renders don't
  // re-parse the in-flight text. (FOX-3611)
  if (markdown) {
    return (
      <>
        <MemoMarkdown>{message}</MemoMarkdown>
        {message.length > 0 && (
          <span
            className="inline-block h-4 w-[2px] -mb-0.5 ml-0.5 bg-foreground/60 animate-pulse align-baseline"
            aria-hidden="true"
          />
        )}
        {!!render && render(message)}
      </>
    );
  }

  // Non-markdown path (e.g. the annotate ask-AI popover) keeps the cheap
  // char-by-char typewriter effect over a plain <span>.
  return (
    <>
      <TypingEffect text={message} idempotencyID={idempotencyID} />
      {!!render && render(message)}
    </>
  );
};
export const AiDelta = React.memo(AiDeltaComponent);

export const TypingEffectComponent = ({
  text,
  typingSpeed = 1, //this is a delay between rendering each character in ms
  idempotencyID,
}: {
  text: string;
  typingSpeed?: number;
  idempotencyID?: string;
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const index = useRef(0);

  useEffect(() => {
    if (!idempotencyID) {
      // Means it is not incremental, reset every time the text is changing
      setDisplayedText("");
      index.current = 0;
    }

    const timer = setInterval(() => {
      if (index.current < text.length) {
        setDisplayedText(text.substring(0, index.current + 1));
        index.current = index.current + 1;
      } else {
        clearInterval(timer);
      }
    }, typingSpeed);

    return () => clearInterval(timer);
  }, [text]);

  useEffect(() => {
    // Reset when the ID changes
    if (idempotencyID) {
      setDisplayedText("");
      index.current = 0;
    }
  }, [idempotencyID]);

  return <span>{displayedText}</span>;
};
export const TypingEffect = React.memo(TypingEffectComponent);
